
import { useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import ReviewForm from "@/components/reviews/ReviewForm";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { useToast } from "@/components/ui/use-toast";

const Submit = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasCompletedProfile, setHasCompletedProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { toast } = useToast();
  
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        
        if (sessionData.session) {
          console.log("Submit page - User is authenticated:", sessionData.session.user.id);
          setIsAuthenticated(true);
          setUserId(sessionData.session.user.id);
          
          // Check if the user has completed their profile
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('full_name, nickname, country')
            .eq('id', sessionData.session.user.id)
            .single();
          
          if (!profileError && profileData?.full_name && profileData?.nickname && profileData?.country) {
            console.log("Submit page - Profile is complete:", profileData);
            setHasCompletedProfile(true);
          } else {
            console.log("Submit page - Profile incomplete or error:", profileError || profileData);
            navigate('/signup/complete');
            return;
          }
        } else {
          console.log("Submit page - No active session");
          setIsAuthenticated(false);
          setUserId(null);
        }
      } catch (error) {
        console.error("Submit page - Error checking auth status:", error);
        toast({
          variant: "destructive",
          title: t('error'),
          description: t('errorFetchingReviews'),
        });
      } finally {
        setLoading(false);
      }
    };
    
    checkSession();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Submit page - Auth state changed:", event, session?.user?.id);
      setIsAuthenticated(!!session);
      setUserId(session?.user?.id || null);
      
      if (!session) {
        setHasCompletedProfile(false);
      } else if (event === 'SIGNED_IN') {
        checkSession();
      }
    });
    
    return () => subscription.unsubscribe();
  }, [toast, t, navigate]);
  
  if (loading) {
    return (
      <MainLayout>
        <div className="container py-8 max-w-3xl">
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <MainLayout>
        <div className="container py-8 max-w-3xl">
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('auth.loginRequired')}
            </AlertDescription>
            <div className="flex gap-4 mt-4">
              <Button onClick={() => navigate('/login')}>
                {t('common.login')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/signup')}>
                {t('common.signup')}
              </Button>
            </div>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  if (!hasCompletedProfile) {
    return (
      <MainLayout>
        <div className="container py-8 max-w-3xl">
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {t('profile.completeProfile')}
            </AlertDescription>
            <Button onClick={() => navigate('/signup/complete')} className="mt-4">
              {t('profile.completeProfileBtn')}
            </Button>
          </Alert>
        </div>
      </MainLayout>
    );
  }

  // This is the main return when authenticated and profile is completed
  return (
    <MainLayout>
      <div className="container py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>{t('form.submit')}</CardTitle>
            <CardDescription>
              {t('home.shareExperience')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {userId && (
              <div className="review-form-container">
                <ReviewForm userId={userId} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default Submit;
